"use client";

/**
 * NewForm design-system Shelf.
 *
 * A titled row of things you scroll sideways — top ads, a video library, a
 * what's-new strip. Not `MediaCarousel`, which shows one slide at a time with
 * a counter and dots; this is the many-at-once shape, and "shelf" is the word
 * the type scale already uses for it.
 *
 *   <Shelf title="What's working" subtitle="The best ads right now.">
 *     {ads.map((ad) => <ShelfItem key={ad.id}><AdTile ad={ad} /></ShelfItem>)}
 *   </Shelf>
 *
 * IT OWNS ITS HEADING, which is the point rather than a convenience. A shelf
 * dropped inside a section that already had one produced two headings saying
 * the same thing in different words — "What's working" above "Top ads", each
 * with its own paragraph. One component owning the title means there is
 * nowhere for the second one to come from.
 *
 * EMBLA DRIVES THE MOTION. This was a native scroll container with a
 * hand-written trapezoidal glide, and the three defects that took to find are
 * the argument for the library: snapping pulled the leading gutter out of view
 * so the row started 13 points left of its own heading, the scroll padding
 * that fixes it had to be repeated at every call site, and the sentinels used
 * to decide whether an arrow was live were themselves snap targets. Embla
 * answers all three as options — `align`, `containScroll`, `slidesToScroll` —
 * and hands over pointer drag, which a scroll container never gives a mouse.
 *
 * WHAT IT COSTS, stated because it was a real trade. Embla exposes `duration`
 * and nothing else about the motion: it is a friction model, decelerating the
 * whole way, and there is no easing hook. The constant-velocity coast the
 * hand-rolled version had is not reachable from here. If that feel turns out
 * to matter more than the maintenance, the trapezoid is in this file's
 * history.
 */

import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { Hint, SectionTitle } from "./text";

/**
 * How many tiles one arrow press advances at rail width.
 *
 * Also how many are centred, which is not a coincidence: Embla centres the
 * *snap unit*, so a step of two centres a pair and the row comes out with four
 * tiles showing and uneven peeks. Three is the number that fits the column
 * with a slice of its neighbours on either side, so three is also the step.
 * Changing one of those without the other pulls the row off centre.
 */
const STEP = 3;

/**
 * Embla's own unit, not milliseconds — it scales the friction, and higher is
 * slower. The default of 25 arrives faster than this surface wants for a row
 * of video.
 */
const DURATION = 36;

export function Shelf({
  title,
  subtitle,
  children,
  className,
  frameClassName,
  trackClassName,
}: {
  title: string;
  /** One line of context under the title. */
  subtitle?: ReactNode;
  children: ReactNode;
  className?: string;
  /**
   * The box the shelf occupies — a width cap, and centring it.
   *
   * Separate from `trackClassName` because they are different boxes and the
   * arrows belong to this one. Capping the scrolling viewport alone leaves the
   * arrows positioned against the full-width wrapper, so they sit out at the
   * column edges with a gulf between them and the tiles they move.
   */
  frameClassName?: string;
  /**
   * Bleed for the viewport, when the shelf sits in a padded parent.
   *
   * Whether a shelf bleeds is the caller's call, and it usually differs by
   * width: under a thumb a row that stops short of the screen reads as a list
   * that ended, while at desk width a row running past the blocks above it is
   * the one thing on the page ignoring the column. So the usual value is a
   * negative margin and a matching padding, dropped at the breakpoint where
   * the page gains a column.
   */
  trackClassName?: string;
}) {
  const [viewport, embla] = useEmblaCarousel({
    /*
      CENTRED AT EVERY WIDTH, which is one rule rather than two layouts.

      The shelf always centres on a tile and always shows part of its
      neighbours on both sides, so it reads the same on a phone and at a desk —
      a phone simply has room for one in the middle and a desk for three. The
      peek on both sides is the thing doing the work: it says there is more in
      either direction without a tile ever sitting awkwardly flush against one
      edge.

      `containScroll: false` on a phone is what lets the first and last tile
      centre too: there the shelf is the whole screen, so a centred first tile
      with a sliver beside it reads as the start of a row rather than as a gap.
      At rail width the shelf sits in a column alongside other sections, and
      opening with empty space to the left of the first tile reads as a broken
      margin — so there the ends are pinned and only the middle centres.

      The one thing that does change with width is how far a press travels,
      because a screen showing three should not advance by one.
    */
    align: "center",
    containScroll: false,
    slidesToScroll: 1,
    duration: DURATION,
    breakpoints: {
      "(min-width: 1024px)": {
        slidesToScroll: STEP,
        // Trim the end snaps at rail width, so the shelf opens with its first
        // tile against the column edge rather than centred with a gap to its
        // left. The middle of the row still centres; only the two ends are
        // pinned, which is what stops a shelf from starting with empty space
        // where a page expects a margin.
        containScroll: "trimSnaps",
      },
    },
  });

  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const read = useCallback((api: NonNullable<typeof embla>) => {
    setCanPrev(api.canScrollPrev());
    setCanNext(api.canScrollNext());
  }, []);

  useEffect(() => {
    if (!embla) return;
    read(embla);
    // `reInit` matters as much as `select`: tiles arrive from a query, and a
    // shelf that measured itself while still empty has both arrows off.
    embla.on("select", read).on("reInit", read);
    return () => {
      embla.off("select", read).off("reInit", read);
    };
  }, [embla, read]);

  return (
    <section
      className={cn("group/shelf flex flex-col gap-nf-ds-item", className)}
    >
      <div className="flex flex-col gap-nf-ds-1">
        {/* `SectionTitle`, not `Rail`. This is a section heading and the
            design system has a role for that; `Rail` is the mono uppercase
            eyebrow, meant for table heads and group labels, and at 10px with
            0.12em of tracking it is hard to read as a heading — which is the
            job it had been quietly given here. */}
        <SectionTitle>{title}</SectionTitle>
        {subtitle ? <Hint muted>{subtitle}</Hint> : null}
      </div>

      <div className={cn("relative", frameClassName)}>
        <div ref={viewport} className={cn("overflow-hidden", trackClassName)}>
          {/* `touch-pan-y` so a vertical swipe still scrolls the page: Embla
              claims the horizontal axis and must not claim the other one. */}
          {/* The gap is half on each side of every slide, and the track is
              flush with the viewport.

              Three versions of this were wrong, each instructively. Dropping
              the padding from the first slide makes it wider than the rest by
              the gap — invisible at a fixed tile width, fatal once slides are
              a fraction. Putting the whole gap on one side makes every slide
              asymmetric, so a centred group sits half a gap off centre. And
              cancelling the padding with a negative margin on the track, which
              is what shadcn does, moves the container out of line with the
              viewport — Embla centres against the viewport, so the whole row
              ends up offset by the margin.

              Half each side and no margin: slides share a centre with their
              tiles, the container shares an edge with the viewport, and the
              only cost is half a gap of padding at each end of the row. */}
          <div className="flex touch-pan-y">{children}</div>
        </div>

        {canPrev || canNext ? (
          <>
            <Arrow
              side="start"
              disabled={!canPrev}
              onClick={() => embla?.scrollPrev()}
            />
            <Arrow
              side="end"
              disabled={!canNext}
              onClick={() => embla?.scrollNext()}
            />
          </>
        ) : null}
      </div>
    </section>
  );
}

/**
 * One tile's slot on the shelf.
 *
 * The gap lives here as the slide's own left padding rather than as a flex
 * `gap` on the track, because Embla measures the container's children to find
 * its snap points and a gap belongs to none of them — with `gap` the snaps
 * drift a little further out of true with every tile.
 *
 * Half of `block` on each side, which comes to `block` between any two tiles
 * — the same role that separates blocks anywhere else, rather than a number
 * picked for this row. Tiles on a shelf are blocks side by side, and
 * one of the reasons a shelf reads as crowded is a gap chosen to fit more in.
 *
 * Pass a `basis` here to size slides as a fraction of the shelf —
 * `lg:basis-1/3` for three across. Every slide carries the same padding, so
 * the fractions come out equal.
 */
export function ShelfItem({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
  /**
   * The box the shelf occupies — a width cap, and centring it.
   *
   * Separate from `trackClassName` because they are different boxes and the
   * arrows belong to this one. Capping the scrolling viewport alone leaves the
   * arrows positioned against the full-width wrapper, so they sit out at the
   * column edges with a gulf between them and the tiles they move.
   */
  frameClassName?: string;
}) {
  return (
    <div className={cn("min-w-0 shrink-0 grow-0 px-nf-ds-item", className)}>
      {children}
    </div>
  );
}

/**
 * One of the two pointer affordances.
 *
 * Opacity and transform only, so the fade is a compositor job. It is `hidden`
 * below the pointer breakpoint rather than merely transparent: an invisible
 * button over the first tile still swallows the tap meant for it, and a touch
 * screen has the drag instead.
 */
function Arrow({
  side,
  disabled,
  onClick,
}: {
  side: "start" | "end";
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={side === "start" ? "Scroll back" : "Scroll forward"}
      className={cn(
        "absolute top-1/2 z-10 hidden -translate-y-1/2 cursor-pointer place-items-center lg:grid",
        side === "start" ? "left-nf-ds-2" : "right-nf-ds-2",
        "size-[36px] rounded-nf-ds-pill",
        "bg-nf-ds-surface-overlay text-nf-ds-fg shadow-nf-ds-overlay",
        "ring-1 ring-nf-ds-surface-overlay-ring ring-inset",
        // Present only while the pointer is on the shelf, and never in the way
        // of the tile underneath when it is not.
        "opacity-0 transition-[opacity,transform,scale] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)]",
        "group-hover/shelf:opacity-100 focus-visible:opacity-100",
        "focus-visible:ring-2 focus-visible:ring-nf-ds-brand",
        "active:scale-[0.94] motion-reduce:transition-none motion-reduce:active:scale-100",
        "disabled:pointer-events-none disabled:opacity-0",
      )}
    >
      {side === "start" ? (
        <ChevronLeft className="size-[18px]" strokeWidth={2.2} />
      ) : (
        <ChevronRight className="size-[18px]" strokeWidth={2.2} />
      )}
    </button>
  );
}
